import { memo, useCallback } from 'react';

import { useSelector } from 'react-redux';

import Button from '@atlaskit/button/custom-theme-button';
import Select from '@atlaskit/select';

import {
  UsePipelineDefinitions,
  usePipelineDefinitions as defaultUsePipelineDefinitions,
} from 'src/components/pipelines/hooks/use-pipeline-definitions';
import { Repository } from 'src/components/pipelines/models';
import { getRepository } from 'src/components/pipelines/redux/selectors/pipelines';

import { PipelineDefinition, PipelineDefinitionOption } from '../types';

import { Label, SelectorWrapper } from './styled';

type Props = {
  configurationUrl: string;
  target: string;
  pipelineDefinitionOption: PipelineDefinitionOption | undefined;
  onChange: (pipelineDefinition: PipelineDefinitionOption | any) => void;
  usePipelineDefinitionsHook?: (
    repository: Repository,
    target: string
  ) => UsePipelineDefinitions;
};

const RunPipelineSelector = ({
  configurationUrl,
  target,
  pipelineDefinitionOption,
  onChange,
  usePipelineDefinitionsHook,
}: Props) => {
  const repository = useSelector(getRepository);
  const usePipelineDef =
    usePipelineDefinitionsHook || defaultUsePipelineDefinitions;
  const { loading, data } = usePipelineDef(repository, target);

  const createOption = useCallback((pipelineDefinition: PipelineDefinition) => {
    const { type, pattern, destinationPattern } = pipelineDefinition;
    const p = pattern ? `: ${pattern}` : '';
    const dP = pattern && destinationPattern ? `:${destinationPattern}` : '';
    const label = `${type}${p}${dP}`;
    return {
      label,
      value: JSON.stringify({ type, pattern, destinationPattern }),
      pipelineDefinition,
    };
  }, []);

  return (
    <>
      <SelectorWrapper>
        <Label>Pipeline</Label>
        <Select<PipelineDefinitionOption>
          isDisabled={!target}
          isLoading={loading}
          onChange={onChange}
          options={data.map((item: PipelineDefinition) => createOption(item))}
          value={pipelineDefinitionOption}
          placeholder="Select a pipeline"
          defaultOptions
          noOptionsMessage={() => 'No pipelines found'}
          id="run-pipeline-selector-select"
          instanceId="run-pipeline-selector-select"
        />
      </SelectorWrapper>
      <Button
        href={configurationUrl}
        target="_blank"
        spacing="none"
        appearance="link"
        isDisabled={!target}
      >
        See configuration
      </Button>
    </>
  );
};

export default memo(RunPipelineSelector);
